import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Check, BookOpen } from "lucide-react";
import api, { formatError } from "@/lib/api";

import { gradeSubjects } from "@/lib/subjects";
const monthAhead = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

export default function ChildAccess({ child, onSaved }) {
  const active = child.paid_until && new Date(child.paid_until) > new Date() ? child.paid_subjects || [] : [];
  const [open, setOpen] = useState(false);
  const now = new Date();
  const ru = child.reading_until || {};
  const activeR = ["en", "fr"].filter((l) => ru[l] && new Date(ru[l]) > now);
  const [subs, setSubs] = useState(active);
  const [rd, setRd] = useState(activeR);
  const [until, setUntil] = useState(monthAhead());
  const [busy, setBusy] = useState(false);
  const save = async (list, langs) => {
    setBusy(true);
    try {
      await api.post(`/admin/children/${child.id}/access`, { subjects: list, valid_until: until, reading: langs });
      toast.success(list.length || langs.length ? `Access released for ${child.name}` : `Access removed for ${child.name}`);
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const toggle = (s) => setSubs(subs.includes(s) ? subs.filter((x) => x !== s) : [...subs, s]);
  const toggleR = (l) => setRd(rd.includes(l) ? rd.filter((x) => x !== l) : [...rd, l]);
  const label = [active.length && `${active.join(", ")} until ${new Date(child.paid_until).toLocaleDateString()}`,
    activeR.length && `Reading ${activeR.map((l) => l.toUpperCase()).join("+")} until ${new Date(ru[activeR[0]]).toLocaleDateString()}`].filter(Boolean).join(" · ");
  return (
    <div className="w-full mt-1" data-testid={`admin-access-${child.id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`admin-access-toggle-${child.id}`}
        className={`text-xs font-semibold inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${label ? "border-green-500 text-green-300" : "border-slate-500 text-slate-300"}`}>
        <KeyRound size={11} /> {label ? `${child.name}: ${label}` : `${child.name}: no access — release`}
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2 bg-slate-900 rounded-xl p-3 border border-slate-700">
          {gradeSubjects(child.grade).map((s) => (
            <button key={s} onClick={() => toggle(s)} data-testid={`admin-access-subject-${child.id}-${s}`}
              className={`text-xs rounded-full px-2.5 py-1 border inline-flex items-center gap-1 ${subs.includes(s) ? "bg-green-700 border-green-500" : "border-slate-600"}`}>
              {subs.includes(s) && <Check size={11} />} {s}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-1">Reading kit:</span>
          {["en", "fr"].map((l) => (
            <button key={l} onClick={() => toggleR(l)} data-testid={`admin-access-reading-${child.id}-${l}`}
              className={`text-xs rounded-full px-2.5 py-1 border inline-flex items-center gap-1 ${rd.includes(l) ? "bg-sky-700 border-sky-400" : "border-slate-600"}`}>
              {rd.includes(l) && <Check size={11} />} <BookOpen size={11} /> {l === "en" ? "English" : "Français"}
            </button>
          ))}
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid={`admin-access-until-${child.id}`} className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs" />
          <button disabled={busy || (!subs.length && !rd.length)} onClick={() => save(subs, rd)} data-testid={`admin-access-save-${child.id}`} className="rounded-full bg-green-600 disabled:opacity-40 px-3 py-1 text-xs font-semibold">Release access</button>
          {(active.length > 0 || activeR.length > 0) && <button disabled={busy} onClick={() => save([], [])} data-testid={`admin-access-remove-${child.id}`} className="rounded-full bg-red-600 px-3 py-1 text-xs font-semibold">Remove access</button>}
        </div>
      )}
    </div>
  );
}
